import { useParams } from 'react-router-dom'
import dayjs from 'dayjs'
import { CircularProgress, Typography } from '@mui/material'
import {
    GetConsultationByIdResponseIds,
    useGetIdsConsultationsConsultationId,
    usePutIdsHealthcareservicesHcsIdConsultationsConsultationId,
} from '@toktokdoctech/tokapi-client'
import { useState } from 'react'
import underscore from 'underscore'
import { RouterRouteId } from '../../../routers/_props'
import PlannedConsultationEditor from './PlannedConsultationEditor'
import { ConsultationFormData } from './_props'
import useStyles from './_styles'
import AutoSaveSatusBar from '../../Common/autoSaveSatusBar/AutoSaveSatusBar'
import PlannedConsultationEditorMediaList from './PlannedConsultationEditorMediaList'
import PlannedConsultationDeletion from './PlannedConsultationDeletion'
import { useAppDispatch } from '../../../store'
import { displaySnackbar } from '../../../actions/main'
import useCurrentHcs from '../../../hooks/useCurrentHcs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

export const PlannedConsultationEdition = () => {
    const { id } = useParams<RouterRouteId>()
    const dispatch = useAppDispatch()
    const currentHcs = useCurrentHcs()
    const classes = useStyles()
    const { data, isFetching, isError, error } = useGetIdsConsultationsConsultationId(
        id || 'consultation id should be defined',
        { query: { enabled: !!id } },
    )
    const [autoSaveStatus, setAutoSaveStatus] = useState({
        autoSaving: false,
        autoSaved: true,
    })

    const { mutate } = usePutIdsHealthcareservicesHcsIdConsultationsConsultationId({
        mutation: {
            onMutate: () => {
                setAutoSaveStatus({
                    autoSaving: true,
                    autoSaved: false,
                })
            },
            onSuccess: () => {
                dispatch(
                    displaySnackbar(
                        'success',
                        'La téléconsultation prévue a bien été mise à jour',
                        undefined,
                        `[PlannedConsultationEditor] modification consultation préparée ${id}`,
                    ),
                )
                setAutoSaveStatus({
                    autoSaving: false,
                    autoSaved: true,
                })
            },
            onError: (err) => {
                const errorMessage = err instanceof Error ? err.message : 'Une erreur est survenue'
                dispatch(
                    displaySnackbar(
                        'error',
                        'Une erreur est survenue lors de la mise à jour de la téléconsultation',
                        undefined,
                        `[PlannedConsultationEditor] erreur lors de la modification de la téléconsultation préparée ${id}: ${errorMessage}`,
                    ),
                )
                setAutoSaveStatus({
                    autoSaving: false,
                    autoSaved: false,
                })
            },
        },
    })

    if (isFetching || !id)
        return (
            <div className={classes.PlannedConsultationEditorLoader}>
                <CircularProgress />
                <Typography>Chargement de la téléconsultation planifiée</Typography>
            </div>
        )
    if (isError) throw error
    if (!data) throw new Error('[EditorWrapper] data is falsy!')

    const transformConsultationToLegacyFormat = (
        consultation: GetConsultationByIdResponseIds,
    ): { consultation: ConsultationFormData; media: { pictures: any[]; documents: any[] } } => ({
        consultation: {
            patientId: consultation.patient.id,
            patientLibelle: consultation.patient.fullName,
            requiredExpertId: consultation.requiredExpert?.id ?? '',
            requiredExpertLibelle: consultation.requiredExpert?.fullName ?? '',
            appointmentDate: dayjs.utc(consultation.date).local().format('YYYY-MM-DDTHH:mm'),
        },
        media: {
            pictures: consultation.pictures,
            documents: consultation.documents,
        },
    })
    const { consultation, media } = transformConsultationToLegacyFormat(data)

    const saveUpdatedConsultation = underscore.debounce(async (updatedConsultation: ConsultationFormData) => {
        mutate({
            consultationId: id,
            hcsId: currentHcs.id,
            data: {
                initialAppointmentDate: dayjs(updatedConsultation.appointmentDate, 'YYYY-MM-DDTHH:mm')
                    .utc()
                    .toISOString(),
            },
        })
    }, 500)

    return (
        <PlannedConsultationEditor
            isNewConsultation={false}
            onSave={saveUpdatedConsultation}
            consultationDetail={consultation}
        >
            <PlannedConsultationEditorMediaList id={id} initialMedias={media} />
            <PlannedConsultationDeletion consultationId={id} />
            <AutoSaveSatusBar {...autoSaveStatus} />
        </PlannedConsultationEditor>
    )
}
